import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Switch,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useDisclosure,
    useToast,
    VStack,
    TableContainer,
    useColorMode,
    Avatar,
    Image,
    Checkbox,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    PopoverArrow,
    PopoverCloseButton,
    Stack,
} from '@chakra-ui/react';
import {
    AiOutlineSearch,
    AiOutlineDelete,
    AiOutlineEdit,
    AiOutlinePlus,
    AiOutlineSchedule,
    AiOutlineCheckCircle,
    AiOutlineClockCircle,
    AiOutlineCloseCircle,
    AiOutlineCalendar,
    AiOutlineEye,
    AiOutlineUser,
    AiOutlinePartition,
    AiOutlineLink,
    AiOutlineDisconnect,
    AiOutlineFileText,
    AiOutlineBank,
    AiOutlineFilter,
    AiOutlineWarning,
    AiOutlineSync,
    AiOutlineMinus,
} from 'react-icons/ai';
import { useComercial } from '../../../../contexts/ComercialContext';
import { usePermissions } from '../../../../contexts/AuthPermissionsContext';
import { useNogleTech } from '../../../../contexts/NogleTechContext';
import { useModalMinimizer } from '../../../../contexts/ModalMinimizerContext';
import ConfirmDeleteDialog from '../../../../components/UI/ConfirmDeleteDialog';
import ConfirmActionDialog from '../../../../components/UI/ConfirmActionDialog';
import TablePagination from '../../../../components/UI/TablePagination';
import SearchableSelect from '../../../../components/UI/SearchableSelect';
import AnotacaoFormSection from '../../../../components/UI/AnotacaoFormSection';
import { useLocation, useNavigate } from 'react-router-dom';
import { criarAnotacao } from '../../../../services/anotacoes';

const FORM_INICIAL = {
    nome: '',
    dataInicio: '',
    horaInicio: '00:00',
    dataFim: '',
    horaFim: '00:00',
    tipo: '',
    tipo_id: null,
    responsavel: '',
    assignee_id: null,
    observacao: '',
    ligarPipeline: false,
    cardId: null,
    status: '',
    status_id: null,
    company_id: null,
    empresa: '',
};

// Visual por PAPEL do status (role do banco: neutro = em andamento/pendente ·
// positivo = concluída · negativo = cancelada). O nome do status é 100% cosmético:
// o rótulo exibido vem do próprio status; aqui ficam só cor/ícone/fallback.
const STATUS_META = {
    neutro:   { label: 'Pendente',  cor: '#ECC94B', icon: AiOutlineClockCircle },
    positivo: { label: 'Concluído', cor: '#02EA75', icon: AiOutlineCheckCircle },
    negativo: { label: 'Cancelado', cor: '#FC8181', icon: AiOutlineCloseCircle },
};

// Primeira letra maiúscula para o rótulo da pílula (o nome do dropdown pode vir
// em minúsculas, ex.: "pendente" → "Pendente"); não altera o valor salvo.
const capitalizarRotulo = (txt) => {
    const s = String(txt || '').trim();
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
};

const TIPO_CORES = {
    'Evento':   '#02EA75',
    'Reunião':  '#4299E1',
    'Ligação':  '#9F7AEA',
    'E-mail':   '#4299E1',
    'WhatsApp': '#48BB78',
    'Visita':   '#ED8936',
    'Tarefa':   '#ECC94B',
    'Prazo':    '#FC8181',
    'Entrega':  '#F687B3',
};

const VIEW_FOOTER_BUTTON_PROPS = {
    size: 'xs',
    h: '30px',
    px: 3,
    fontSize: 'xs',
    fontWeight: 700,
    borderRadius: 'lg',
    iconSpacing: 1.5,
    flexShrink: 0,
    whiteSpace: 'nowrap',
};

// --? DetailItem --------------------------------------------------------------
const DetailItem = ({ icon, label, value, valueColor, accent = '#718096', leftElement, ...props }) => {
    const { colorMode } = useColorMode();
    return (
        <Box
            bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
            border="1px solid"
            borderColor="borderPrimary"
            borderRadius="xl"
            p={3}
            transition="all .15s ease"
            _hover={{ borderColor: `${accent}55`, bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100' }}
            {...props}
        >
            <HStack spacing={3} align="center">
                {leftElement || (
                    <Flex
                        flexShrink={0}
                        boxSize="38px"
                        borderRadius="lg"
                        align="center"
                        justify="center"
                        bg={`${accent}1A`}
                        color={accent}
                        border="1px solid"
                        borderColor={`${accent}33`}
                    >
                        <Icon as={icon} boxSize="17px" />
                    </Flex>
                )}
                <Box minW={0}>
                    <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px" mb={0.5}>
                        {label}
                    </Text>
                    <Text fontSize="sm" color={valueColor || 'textPrimary'} fontWeight="700" lineHeight="1.3" noOfLines={2}>
                        {value || '—'}
                    </Text>
                </Box>
            </HStack>
        </Box>
    );
};

const Atividades = () => {
    const { colorMode } = useColorMode();
    const {
        atividades, addAtividade, updateAtividade, deleteAtividade, pipelineCards, pipelines,
        tiposAtividade, fetchTiposAtividade, statusAtividade, fetchStatusAtividade,
    } = useComercial();
    const { employees } = usePermissions();
    const { marcas, loading: marcasLoading, refetchMarcas } = useNogleTech();
    const toast = useToast();
    const location = useLocation();
    const navigate = useNavigate();

    // Modals
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
    const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();

    // Dropdowns do banco: tipos e status vivem no ComercialContext (carregados no
    // login, como resultadosNegocio/canais). Aliases locais mantêm o restante do
    // arquivo legível.
    const tipos = useMemo(() => tiposAtividade || [], [tiposAtividade]);
    const statusList = useMemo(() => statusAtividade || [], [statusAtividade]);

    const resolveTipoCor = useCallback((tipoNome) => {
        const matchingType = (tipos || []).find(t => t.nome === tipoNome);
        return matchingType?.cor || TIPO_CORES[tipoNome] || '#718096';
    }, [tipos]);

    // Papel da atividade (neutro/positivo/negativo) — a ÚNICA fonte de classificação
    // da tela. Ordem: status_role da API → role da opção na lista → flags
    // completed/canceled → radicais no nome (só para dado legado sem papel).
    const papelDeAtividade = useCallback((atv) => {
        if (!atv) return 'neutro';
        const roleNum = (r) => (r === 1 || r === '1') ? 'positivo' : (r === 2 || r === '2') ? 'negativo' : (r === 0 || r === '0') ? 'neutro' : null;
        const direto = roleNum(atv.status_role);
        if (direto) return direto;
        const opt = (statusList || []).find(s =>
            String(s.id) === String(atv.status_id ?? '') ||
            String(s.nome || '').toLowerCase() === String(atv.status || '').toLowerCase()
        );
        const daLista = opt ? roleNum(Number(opt.role)) : null;
        if (daLista) return daLista;
        if (atv.canceled) return 'negativo';
        if (atv.completed) return 'positivo';
        const s = String(atv.status || '').toLowerCase();
        if (s.includes('conclu')) return 'positivo';
        if (s.includes('cancel')) return 'negativo';
        return 'neutro';
    }, [statusList]);

    const [busca, setBusca] = useState('');
    const [filterEmployeeIds, setFilterEmployeeIds] = useState([]);
    const [filterSearch, setFilterSearch] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('todos');
    const [currentPage, setCurrentPage] = useState(1);
    // Paginação configurável compartilhada (TablePagination): 5 por padrão, opções 15/30/50
    const [itemsPerPage, setItemsPerPage] = useState(5);

    const [atividadeEditando, setAtividadeEditando] = useState(null);
    const [atividadeVisualizando, setAtividadeVisualizando] = useState(null);
    const [atividadeExcluindo, setAtividadeExcluindo] = useState(null);
    const [form, setForm] = useState(FORM_INICIAL);
    const [novaAnotacao, setNovaAnotacao] = useState('');

    // Autocomplete state
    const [empresaFocada, setEmpresaFocada] = useState(false);

    const marcasSugeridas = useMemo(() => {
        const query = form.empresa?.trim().toLowerCase();
        if (!query) return [];
        return marcas.filter(m => m.nome?.toLowerCase().includes(query)).slice(0, 8);
    }, [marcas, form.empresa]);

    const pipelineLinkStatus = useCallback((atividade) => {
        if (!atividade?.cardId) return { linked: !!atividade?.ligarPipeline, semFunil: false };
        const card = (pipelineCards || []).find(c => String(c.id) === String(atividade.cardId));
        if (!card) return { linked: true, semFunil: true };
        const hasActiveColumn = (pipelines || []).some(pipe =>
            (pipe.colunas || []).some(col => String(col.id) === String(card.colunaId))
        );
        return { linked: true, semFunil: !hasActiveColumn, card };
    }, [pipelineCards, pipelines]);

    // Atualiza os dropdowns ao entrar na tela (o contexto já carregou no login;
    // aqui é só refresh para pegar opções recém-criadas em Controladores).
    useEffect(() => {
        fetchTiposAtividade();
        fetchStatusAtividade();
    }, [fetchTiposAtividade, fetchStatusAtividade]);

    // Auto-recuperação do formulário: se algum dado de apoio falhou em carregar
    // (tipos/status vazios ou marcas do ERP indisponíveis), tenta buscar de novo
    // no momento em que o usuário abre o modal — nada de dropdown mudo/vazio.
    const garantirDadosDoFormulario = useCallback(() => {
        if ((tiposAtividade || []).length === 0) fetchTiposAtividade();
        if ((statusAtividade || []).length === 0) fetchStatusAtividade();
        if ((marcas || []).length === 0 && !marcasLoading) refetchMarcas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tiposAtividade, statusAtividade, marcas, marcasLoading, fetchTiposAtividade, fetchStatusAtividade]);

    // Uma atividade "é" de um status do dropdown quando bate o id (ou o nome, legado).
    const atividadeTemStatus = useCallback((atv, statusOpt) => (
        String(atv.status_id ?? '') === String(statusOpt.id) ||
        String(atv.status || '').trim().toLowerCase() === String(statusOpt.nome || '').trim().toLowerCase()
    ), []);

    const contarPorStatus = useCallback((statusOpt) => (
        atividades.filter(a => atividadeTemStatus(a, statusOpt)).length
    ), [atividades, atividadeTemStatus]);

    const atividadesFiltradas = useMemo(() => {
        let lista = [...atividades];
        // Filtro por status agora é DINÂMICO: filtroStatus = 'todos' ou o id de uma
        // opção de status_atividade (dos Controladores). Sem pílula fixa.
        if (filtroStatus !== 'todos') {
            const opt = (statusList || []).find(s => String(s.id) === String(filtroStatus));
            lista = opt ? lista.filter(a => atividadeTemStatus(a, opt)) : lista;
        }
        if (filterEmployeeIds.length > 0) {
            lista = lista.filter(a => filterEmployeeIds.includes(String(a.assignee_id)));
        }
        if (busca.trim()) {
            const q = busca.toLowerCase();
            lista = lista.filter(a =>
                a.nome?.toLowerCase().includes(q) ||
                a.responsavel?.toLowerCase().includes(q) ||
                a.tipo?.toLowerCase().includes(q) ||
                a.empresa?.toLowerCase().includes(q)
            );
        }
        return lista.sort((a, b) => {
            const aAndamento = papelDeAtividade(a) === 'neutro';
            const bAndamento = papelDeAtividade(b) === 'neutro';
            if (aAndamento && !bAndamento) return -1;
            if (bAndamento && !aAndamento) return 1;
            return new Date(a.dataInicio || 0) - new Date(b.dataInicio || 0);
        });
    }, [atividades, filtroStatus, busca, filterEmployeeIds, papelDeAtividade, statusList, atividadeTemStatus]);

    const totalPages = Math.max(1, Math.ceil(atividadesFiltradas.length / itemsPerPage));
    const paginatedAtividades = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return atividadesFiltradas.slice(start, start + itemsPerPage);
    }, [atividadesFiltradas, currentPage, itemsPerPage]);

    // Reset pagination when search, status, tipo, or employee filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [busca, filtroStatus, filterEmployeeIds]);
    useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [currentPage, totalPages]);

    // Auto-open activity when navigated from Pipeline with ID
    useEffect(() => {
        const state = location.state;
        const queryParams = new URLSearchParams(location.search);
        const actId = queryParams.get('openActivityId') || state?.openActivityId;
        const mode = queryParams.get('mode') || state?.mode || 'view';

        if (actId) {
            const found = atividades.find(a => String(a.id) === String(actId));
            if (found) {
                if (mode === 'view') {
                    setTimeout(() => handleVisualizar(found), 50);
                } else if (mode === 'edit') {
                    setTimeout(() => handleAbrir(found), 50);
                } else if (mode === 'delete') {
                    setTimeout(() => handleAbrirExcluir(found), 50);
                }
                // Clear state
                navigate(location.pathname, { replace: true, state: {} });
            }
        }
    }, [location, atividades, navigate]);

    // Pílulas de filtro DINÂMICAS: "Todas" + uma por status cadastrado em Controladores
    // (statusList). Removida uma opção lá, some a pílula aqui; criada, aparece.
    const filtrosStatus = useMemo(() => ([
        { id: 'todos', label: 'Todas', count: atividades.length },
        ...(statusList || []).map(s => ({
            id: s.id,
            label: capitalizarRotulo(s.nome),
            count: contarPorStatus(s),
        })),
    ]), [atividades.length, statusList, contarPorStatus]);

    // -- Visualizar ----------------------------------------------?
    const handleVisualizar = useCallback((atv) => {
        setAtividadeVisualizando(atv);
        onViewOpen();
    }, [onViewOpen]);

    const handleFecharVisualizar = useCallback(() => {
        setAtividadeVisualizando(null);
        onViewClose();
    }, [onViewClose]);

    // -- Editar --------------------------------------------------?
    // Regra única de bloqueio de edição (ficha E botão rápido da tabela):
    // cancelada não pode ser editada; data já passada também não.
    const motivoBloqueioEdicao = useCallback((atv) => {
        if (!atv) return null;
        if (papelDeAtividade(atv) === 'negativo') {
            return 'Atividade cancelada não pode ser editada';
        }
        const fim = atv.dataFim || atv.dataInicio;
        if (fim && fim < new Date().toLocaleDateString('en-CA')) {
            return 'Eventos passados não podem ser editados';
        }
        return null;
    }, [papelDeAtividade]);

    const handleAbrir = useCallback((atividade = null) => {
        if (atividade) {
            const motivo = motivoBloqueioEdicao(atividade);
            if (motivo) {
                toast({ title: motivo, status: 'warning', duration: 2500 });
                return;
            }
        }
        garantirDadosDoFormulario();
        setNovaAnotacao('');
        setAtividadeEditando(atividade);
        if (atividade) {
            setForm({
                nome: atividade.nome || '',
                dataInicio: atividade.dataInicio || '',
                horaInicio: atividade.horaInicio || '00:00',
                dataFim: atividade.dataFim || '',
                horaFim: atividade.horaFim || '00:00',
                tipo: atividade.tipo || '',
                tipo_id: atividade.tipo_id || null,
                responsavel: atividade.responsavel || '',
                assignee_id: atividade.assignee_id || null,
                observacao: atividade.observacao || '',
                ligarPipeline: !!atividade.ligarPipeline,
                cardId: atividade.cardId || null,
                status: atividade.status || '',
                status_id: atividade.status_id || null,
                company_id: atividade.company_id || null,
                empresa: atividade.empresa || '',
            });
        } else {
            setForm(FORM_INICIAL);
        }
        onOpen();
    }, [onOpen, motivoBloqueioEdicao, toast, garantirDadosDoFormulario]);

    // Deep-link:
    //  ? /comercial/atividades?card=ID            ? nova atividade já vinculada ao negócio
    //  ? /comercial/atividades?new=1&date=...&dateFim=... ? abre o MESMO modal de criação
    //    (usado pelo botão "Criar evento" / clique no dia do Calendário ? DRY: um único modal)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const cardParam = params.get('card');
        const newParam = params.get('new');
        const dateParam = params.get('date');
        const dateFimParam = params.get('dateFim');
        // Horários vindos do Calendário (clique/seleção num slot das visões Dia/Semana)
        const horaParam = params.get('hora');
        const horaFimParam = params.get('horaFim');
        if (!cardParam && !newParam && !dateParam) return;
        const card = cardParam ?(pipelineCards || []).find(c => String(c.id) === String(cardParam)) : null;
        const soData = (v) => (v ? String(v).slice(0, 10) : '');
        garantirDadosDoFormulario();
        setNovaAnotacao('');
        setAtividadeEditando(null);
        setForm({
            ...FORM_INICIAL,
            ...(dateParam ?{
                dataInicio: soData(dateParam),
                dataFim: soData(dateFimParam) || soData(dateParam),
                ...(horaParam ?{ horaInicio: horaParam } : {}),
                ...(horaFimParam ?{ horaFim: horaFimParam } : {}),
            } : {}),
            ...(cardParam ?{
                cardId: String(cardParam),
                ligarPipeline: true,
                company_id: card?.company_id || null,
                empresa: card?.empresa || '',
            } : {}),
        });
        onOpen();
        navigate('/comercial/atividades', { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, pipelineCards]);

    const { minimizeModal, removeMinimizedModal, consumePendingRestore } = useModalMinimizer();

    const handleMinimizar = useCallback(() => {
        onClose();
        const modalId = 'atividade-modal';
        const title = atividadeEditando ? 'Editar Atividade' : 'Criar Evento';
        const subtitle = form.nome || form.empresa || 'Rascunho de Atividade';
        minimizeModal({
            id: modalId,
            title,
            subtitle,
            icon: AiOutlineCalendar,
            route: '/comercial/atividades',
            formData: { form, atividadeEditando, novaAnotacao },
            onExpand: () => {
                onOpen();
            },
            onClose: () => {
                setAtividadeEditando(null);
                setForm(FORM_INICIAL);
                setNovaAnotacao('');
            }
        });
    }, [onClose, onOpen, atividadeEditando, form, novaAnotacao, minimizeModal]);

    useEffect(() => {
        const pendingData = consumePendingRestore('atividade-modal');
        if (pendingData) {
            if (pendingData.form) setForm(pendingData.form);
            if (pendingData.atividadeEditando !== undefined) setAtividadeEditando(pendingData.atividadeEditando);
            if (pendingData.novaAnotacao !== undefined) setNovaAnotacao(pendingData.novaAnotacao);
            onOpen();
        }
    }, [consumePendingRestore, onOpen]);

    const handleFechar = useCallback(() => {
        setAtividadeEditando(null);
        setForm(FORM_INICIAL);
        setNovaAnotacao('');
        removeMinimizedModal('atividade-modal');
        onClose();
    }, [onClose, removeMinimizedModal]);

    const handleEditarDoView = useCallback(() => {
        handleFecharVisualizar();
        handleAbrir(atividadeVisualizando);
    }, [atividadeVisualizando, handleFecharVisualizar, handleAbrir]);

    const handleSalvar = useCallback(async () => {
        if (!form.nome.trim()) {
            toast({ title: 'Nome do evento obrigatório', status: 'warning', duration: 2000 });
            return;
        }
        if (!form.dataInicio) {
            toast({ title: 'Data de início obrigatória', status: 'warning', duration: 2000 });
            return;
        }
        if (!form.tipo && !form.tipo_id) {
            toast({ title: 'Tipo obrigatório', status: 'warning', duration: 2000 });
            return;
        }
        if (!form.status && !form.status_id) {
            toast({ title: 'Status obrigatório', status: 'warning', duration: 2000 });
            return;
        }
        if (!form.assignee_id) {
            toast({ title: 'Responsável precisa estar cadastrado no sistema', status: 'warning', duration: 2500 });
            return;
        }
        if (!form.company_id) {
            toast({
                title: form.empresa?.trim() ?'Empresa precisa ser uma marca cadastrada' : 'Empresa / marca obrigatória',
                description: form.empresa?.trim() ?'Selecione uma marca da lista (não vale texto livre).' : undefined,
                status: 'warning', duration: 3000,
            });
            return;
        }
        if (!atividadeEditando) {
            const dataInicioStr = `${form.dataInicio}T${form.horaInicio || '00:00'}:00`;
            const dataInicioObj = new Date(dataInicioStr);
            const limiteInferior = new Date(Date.now() - 2 * 60 * 1000); // tolerância de 2 minutos
            if (dataInicioObj < limiteInferior) {
                toast({ title: 'A data e hora de início não podem ser anteriores ao momento atual', status: 'warning', duration: 3000 });
                return;
            }
        }
        const payload = {
            ...form,
            dataFim: form.dataFim || form.dataInicio,
            observacao: atividadeEditando ?atividadeEditando.observacao : '',
        };
        try {
            let atividadeId;
            if (atividadeEditando) {
                await updateAtividade(atividadeEditando.id, payload);
                atividadeId = atividadeEditando.id;
            } else {
                const atividadeCriada = await addAtividade(payload);
                atividadeId = atividadeCriada?.id;
            }

            let anotacaoFalhou = false;
            if (novaAnotacao.trim()) {
                try {
                    await criarAnotacao({
                        refType: 'atividade',
                        refId: atividadeId,
                        text: novaAnotacao,
                    });
                } catch (err) {
                    anotacaoFalhou = true;
                    console.error('Atividade salva, mas a anotação não foi registrada:', err);
                }
            }

            handleFechar();
            toast(anotacaoFalhou
                ? {
                    title: atividadeEditando ?'Atividade atualizada' : 'Atividade criada',
                    description: 'O registro foi salvo, mas a anota\u00e7\u00e3o n\u00e3o p\u00f4de ser adicionada \u00e0 linha do tempo.',
                    status: 'warning',
                    duration: 4500,
                    isClosable: true,
                }
                : {
                    title: atividadeEditando ?'Atividade atualizada' : 'Atividade criada',
                    status: 'success',
                    duration: 2000,
                });
        } catch (err) {
            console.error('Erro ao salvar atividade:', err);
            toast({ title: 'Erro ao salvar', status: 'error', duration: 3500 });
        }
    }, [form, novaAnotacao, atividadeEditando, addAtividade, updateAtividade, handleFechar, toast]);

    // -- Excluir --------------------------------------------------?
    const handleAbrirExcluir = useCallback((atv) => {
        setAtividadeExcluindo(atv);
        onDelOpen();
    }, [onDelOpen]);

    const handleExcluirDoView = useCallback(() => {
        handleFecharVisualizar();
        handleAbrirExcluir(atividadeVisualizando);
    }, [atividadeVisualizando, handleFecharVisualizar, handleAbrirExcluir]);

    const handleConfirmarExcluir = useCallback(async () => {
        if (atividadeExcluindo) {
            try {
                await deleteAtividade(atividadeExcluindo.id);
                toast({ title: 'Atividade removida', status: 'info', duration: 2000 });
            } catch (err) {
                console.error(err);
                toast({ title: 'Erro ao excluir', status: 'error', duration: 3000 });
            }
        }
        setAtividadeExcluindo(null);
        onDelClose();
    }, [atividadeExcluindo, deleteAtividade, onDelClose, toast]);

    // Acha a opção de status pelo PAPEL (1 positivo/concluir · 2 negativo/cancelar);
    // fallback pelo radical no nome para bancos ainda sem papel definido.
    const statusPorPapel = useCallback((papel) => {
        const alvoRole = papel === 'positivo' ? 1 : 2;
        const radical = papel === 'positivo' ? 'conclu' : 'cancel';
        return (statusList || []).find(s => Number(s.role) === alvoRole)
            || (statusList || []).find(s => String(s.nome).toLowerCase().includes(radical))
            || null;
    }, [statusList]);

    const handleConcluir = useCallback(async (id) => {
        try {
            const atv = (atividades || []).find(a => a.id === id);
            if (!atv) return;
            const concluidoOpt = statusPorPapel('positivo');
            if (!concluidoOpt) {
                toast({ title: 'Nenhum status "concluído" configurado', description: 'Defina o papel de um status em Controladores.', status: 'warning', duration: 3500 });
                return;
            }
            // Mescla a atividade inteira para não apagar nome/datas/tipo/empresa ao concluir.
            await updateAtividade(id, {
                ...atv,
                status: concluidoOpt.id,
                status_id: concluidoOpt.id,
            });
            toast({ title: 'Atividade concluída', status: 'success', duration: 2000 });
        } catch (err) {
            console.error(err);
            toast({ title: 'Erro ao concluir', status: 'error', duration: 3000 });
        }
    }, [updateAtividade, statusPorPapel, toast, atividades]);

    const handleCancelar = useCallback(async (id) => {
        try {
            const atv = (atividades || []).find(a => a.id === id);
            if (!atv) return;
            const canceladoOpt = statusPorPapel('negativo');
            if (!canceladoOpt) {
                toast({ title: 'Nenhum status "cancelado" configurado', description: 'Defina o papel de um status em Controladores.', status: 'warning', duration: 3500 });
                return;
            }
            // Mescla a atividade inteira para não apagar nome/datas/tipo/empresa ao cancelar.
            await updateAtividade(id, {
                ...atv,
                status: canceladoOpt.id,
                status_id: canceladoOpt.id,
            });
            toast({ title: 'Atividade cancelada', status: 'info', duration: 2000 });
        } catch (err) {
            console.error(err);
            toast({ title: 'Erro ao cancelar', status: 'error', duration: 3000 });
        }
    }, [updateAtividade, statusPorPapel, toast, atividades]);

    // Confirmação de ações de 1 clique (concluir/cancelar) — evita cliques acidentais
    const [acaoConfirmar, setAcaoConfirmar] = useState(null); // { tipo: 'concluir'|'cancelar', atividade, fecharFicha }

    const executarAcaoConfirmada = useCallback(async () => {
        if (!acaoConfirmar) return;
        const { tipo, atividade, fecharFicha } = acaoConfirmar;
        if (tipo === 'concluir') await handleConcluir(atividade.id);
        else await handleCancelar(atividade.id);
        if (fecharFicha) handleFecharVisualizar();
        setAcaoConfirmar(null);
    }, [acaoConfirmar, handleConcluir, handleCancelar, handleFecharVisualizar]);

    const diasAte = (data) => {
        if (!data) return null;
        return Math.ceil((new Date(data) - new Date()) / (1000 * 60 * 60 * 24));
    };

    const fmtData = (d) => {
        if (!d) return '—';
        return new Date(d + 'T12:00:00').toLocaleDateString('pt-BR');
    };

    // Badge de status: cor/ícone vêm do PAPEL; o rótulo é o NOME real do status
    // (cosmético, definido em Controladores) — cai no rótulo padrão do papel se vazio.
    const getStatusMeta = (atv) => {
        const base = STATUS_META[papelDeAtividade(atv)] || STATUS_META.neutro;
        const nomeReal = String(atv?.status || '').trim();
        const corReal = atv?.status_cor || base.cor;
        return { ...base, cor: corReal, label: nomeReal || base.label };
    };

    // Bloqueio retroativo: atividade cuja data já passou não pode ser concluída/editada
    const atividadePassada = !!atividadeVisualizando && (() => {
        const fim = atividadeVisualizando.dataFim || atividadeVisualizando.dataInicio;
        return fim ?fim < new Date().toLocaleDateString('en-CA') : false;
    })();
    // Edição bloqueada na ficha (mesma regra do botão rápido): cancelada ou passada
    const bloqueioEdicaoView = motivoBloqueioEdicao(atividadeVisualizando);

    return (
        <VStack align="stretch" spacing={6}>
            {/* Header */}
            <Flex justify="space-between" align="center" wrap="wrap" gap={4} w="full">
                <Box flexShrink={0}>
                    <Heading size="lg" color="textPrimary" fontWeight="900">Atividades</Heading>
                </Box>
                <Flex gap={3} wrap="wrap" justify="flex-end" flex="1" minW="0" align="center">
                    <InputGroup w={{ base: 'full', sm: '240px' }} size="sm">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={AiOutlineSearch} color="textSecondary" />
                        </InputLeftElement>
                        <Input
                            placeholder="Buscar atividade..."
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            bg="surface"
                            border="1px solid"
                            borderColor="borderPrimary"
                            _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                            color="textPrimary"
                            borderRadius="xl"
                        />
                    </InputGroup>
                    <Popover placement="bottom-end" closeOnBlur={true}>
                        <PopoverTrigger>
                            <Button
                                size="sm"
                                variant="outline"
                                borderColor="borderPrimary"
                                color="textPrimary"
                                leftIcon={<Icon as={AiOutlineFilter} boxSize={4} />}
                                _hover={{ bg: 'surfaceHover' }}
                                borderRadius="xl"
                                fontWeight="800"
                            >
                                Filtros {filterEmployeeIds.length > 0 && `(${filterEmployeeIds.length})`}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent bg="surface" borderColor="borderPrimary" color="textPrimary" w="320px" borderRadius="xl" _focus={{ boxShadow: 'none' }} zIndex={999}>
                            <PopoverArrow bg="surface" />
                            <PopoverHeader borderColor="borderPrimary" fontWeight="bold" fontSize="sm">
                                Filtrar Funcionários
                            </PopoverHeader>
                            <PopoverCloseButton />
                            <PopoverBody maxH="400px" overflowY="auto" p={0}>
                                <Box p={3} borderBottom="1px solid" borderColor="borderPrimary">
                                    <Input
                                        placeholder="Buscar..."
                                        size="sm"
                                        bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                                        border="1px solid"
                                        borderColor="borderPrimary"
                                        value={filterSearch}
                                        onChange={(e) => setFilterSearch(e.target.value)}
                                        mb={2}
                                        borderRadius="md"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="green"
                                        w="100%"
                                        onClick={() => setFilterEmployeeIds([])}
                                        isDisabled={filterEmployeeIds.length === 0}
                                    >
                                        Limpar Filtros
                                    </Button>
                                </Box>
                                <Stack spacing={0}>
                                    {employees
                                        ?.filter(emp => {
                                            if (!filterSearch) return true;
                                            return emp.name?.toLowerCase().includes(filterSearch.toLowerCase());
                                        })
                                        .map((emp) => {
                                            const isChecked = filterEmployeeIds.includes(String(emp.id));
                                            return (
                                                <Box
                                                    key={emp.id}
                                                    px={4}
                                                    py={2}
                                                    cursor="pointer"
                                                    _hover={{ bg: "surfaceHover" }}
                                                    onClick={() => {
                                                        setFilterEmployeeIds(prev => {
                                                            const sid = String(emp.id);
                                                            if (prev.includes(sid)) return prev.filter(x => x !== sid);
                                                            return [...prev, sid];
                                                        });
                                                    }}
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={3}
                                                >
                                                    <Checkbox
                                                        isChecked={isChecked}
                                                        colorScheme="green"
                                                        borderColor="borderPrimary"
                                                        pointerEvents="none"
                                                    />
                                                    <Avatar size="xs" name={emp.name} src={emp.avatar || emp.url} />
                                                    <Text fontSize="sm" noOfLines={1}>{emp.name}</Text>
                                                </Box>
                                            );
                                        })}
                                    {(!employees || employees.length === 0) && (
                                        <Box p={4} textAlign="center" color="textMuted" fontSize="sm">
                                            Nenhum funcionário encontrado.
                                        </Box>
                                    )}
                                </Stack>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                    <Button
                        leftIcon={<AiOutlinePlus />}
                        size="sm"
                        flexShrink={0}
                        bg="secondary"
                        color="primary"
                        _hover={{ bg: 'secondaryHover' }}
                        fontWeight="800"
                        onClick={() => handleAbrir()}
                    >
                        Nova Atividade
                    </Button>
                </Flex>
            </Flex>

            {/* Filtros por status — dinâmicos, vindos dos dropdowns (Controladores) */}
            <Flex gap={2} wrap="wrap">
                {filtrosStatus.map(f => {
                    const active = String(filtroStatus) === String(f.id);
                    return (
                        <Button
                            key={f.id}
                            size="sm"
                            variant="ghost"
                            border="1px solid" borderColor={active ?'rgba(1,233,117,0.45)' : 'borderSubtle'} bg={active ?'secondarySubtle' : 'transparent'}
                            color={active ?'secondary' : 'textSecondary'}
                            _hover={{ bg: active ?'secondarySubtle' : 'surfaceHover', color: active ?'secondary' : 'textPrimary' }}
                            borderRadius="lg"
                            fontWeight="700"
                            onClick={() => setFiltroStatus(f.id)}
                            rightIcon={
                                <Badge
                                    bg={active ?'secondary' : 'surfaceRaised'}
                                    color={active ?'primary' : 'textSecondary'}
                                    fontSize="xs"
                                    fontWeight="800"
                                    borderRadius="full"
                                    minW="20px"
                                    textAlign="center"
                                >
                                    {f.count}
                                </Badge>
                            }
                        >
                            {f.label}
                        </Button>
                    );
                })}
            </Flex>

            {/* Tabela */}
            <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
                <Flex
                    align="center"
                    gap={3}
                    p={4}
                    bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                    borderBottom="1px solid"
                    borderColor="borderPrimary"
                >
                    <Icon as={AiOutlineSchedule} color="secondary" boxSize="20px" />
                    <Heading size="sm" color="textPrimary" textTransform="uppercase" letterSpacing="1px" fontWeight="800">
                        Lista de Atividades
                    </Heading>
                    <Badge ml="auto" bg="rgba(2,234,117,0.15)" color="secondary" fontWeight="800">
                        {atividadesFiltradas.length}
                    </Badge>
                </Flex>
                <TableContainer>
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Status</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Nome do Evento</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Tipo</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Responsável</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Período</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Pipeline</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px" isNumeric>Ações</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {atividadesFiltradas.length === 0 && (
                                <Tr>
                                    <Td colSpan={7} textAlign="center" py={10} color="textSecondary" borderColor="borderPrimary">
                                        Nenhuma atividade encontrada
                                    </Td>
                                </Tr>
                            )}
                            {paginatedAtividades.map(atv => {
                                const meta = getStatusMeta(atv);
                                const dias = diasAte(atv.dataInicio);
                                const papel = papelDeAtividade(atv);
                                const atrasado = papel === 'neutro' && dias !== null && dias < 0;
                                const cancelada = papel === 'negativo';
                                const pipelineStatus = pipelineLinkStatus(atv);

                                return (
                                    <Tr
                                        key={atv.id}
                                        _hover={{ bg: colorMode === 'dark' ?'whiteAlpha.50' : 'blackAlpha.50' }}
                                        cursor="pointer"
                                        onClick={() => handleVisualizar(atv)}
                                        opacity={cancelada ?0.5 : 1}
                                    >
                                        <Td borderColor="borderPrimary">
                                            <Badge
                                                bg={`${meta.cor}20`}
                                                color={meta.cor}
                                                fontSize="10px"
                                                fontWeight="800"
                                                borderRadius="md"
                                                px={2}
                                                py={1}
                                                display="inline-flex"
                                                alignItems="center"
                                            >
                                                <HStack spacing={1}>
                                                    <Icon as={meta.icon} boxSize="10px" />
                                                    <Text>{meta.label}</Text>
                                                </HStack>
                                            </Badge>
                                        </Td>
                                        <Td borderColor="borderPrimary">
                                            <VStack align="start" spacing={0.5}>
                                                <Text
                                                    fontSize="sm"
                                                    fontWeight="700"
                                                    color="textPrimary"
                                                    textDecoration={cancelada ?'line-through' : 'none'}
                                                    noOfLines={2}
                                                    maxW="240px"
                                                >
                                                    {atv.nome || '—'}
                                                </Text>
                                                {atv.empresa && (
                                                    <Text fontSize="10px" color="textSecondary" fontWeight="600">
                                                        {atv.empresa}
                                                    </Text>
                                                )}
                                            </VStack>
                                        </Td>
                                        <Td borderColor="borderPrimary">
                                            {atv.tipo ?(
                                                <Badge
                                                    bg={`${resolveTipoCor(atv.tipo)}20`}
                                                    color={resolveTipoCor(atv.tipo)}
                                                    fontSize="xs"
                                                    fontWeight="700"
                                                    borderRadius="md"
                                                >
                                                    {atv.tipo}
                                                </Badge>
                                            ) : '—'}
                                        </Td>
                                        <Td borderColor="borderPrimary">
                                            <Text fontSize="xs" color="textPrimary" fontWeight="600">
                                                {atv.responsavel || '—'}
                                            </Text>
                                        </Td>
                                        <Td borderColor="borderPrimary">
                                            <HStack spacing={1}>
                                                <Icon
                                                    as={AiOutlineCalendar}
                                                    color={atrasado ?'red.400' : 'textSecondary'}
                                                    boxSize="12px"
                                                />
                                                <Text
                                                    fontSize="xs"
                                                    color={atrasado ?'red.400' : 'textSecondary'}
                                                    fontWeight={atrasado ?'700' : '400'}
                                                >
                                                    {(() => {
                                                        if (!atv.dataInicio) return '—';
                                                        const hasHoraInicio = atv.horaInicio && atv.horaInicio !== '00:00';
                                                        const hasHoraFim = atv.horaFim && atv.horaFim !== '00:00';

                                                        let str = fmtData(atv.dataInicio);
                                                        if (hasHoraInicio) {
                                                            str += ` ${atv.horaInicio}`;
                                                        }

                                                        if (atv.dataFim && atv.dataFim !== atv.dataInicio) {
                                                            str += ` ? ${fmtData(atv.dataFim)}`;
                                                            if (hasHoraFim) {
                                                                str += ` ${atv.horaFim}`;
                                                            }
                                                        } else if (hasHoraFim && atv.horaFim !== atv.horaInicio) {
                                                            str += ` - ${atv.horaFim}`;
                                                        }
                                                        return str;
                                                    })()}
                                                    {atrasado && ' (atrasado)'}
                                                </Text>
                                            </HStack>
                                        </Td>
                                        <Td borderColor="borderPrimary">
                                            {pipelineStatus.semFunil ?(
                                                <Badge bg="rgba(252,129,129,0.16)" color="#FC8181" border="1px solid" borderColor="rgba(252,129,129,0.35)" fontSize="9px" fontWeight="900" borderRadius="md" px={1.5}>
                                                    <HStack spacing={1}>
                                                        <Icon as={AiOutlineWarning} boxSize="9px" />
                                                        <Text>Sem funil</Text>
                                                    </HStack>
                                                </Badge>
                                            ) : atv.ligarPipeline ?(
                                                <Badge bg="rgba(2,234,117,0.15)" color="secondary" fontSize="9px" fontWeight="800" borderRadius="md" px={1.5}>
                                                    <HStack spacing={1}>
                                                        <Icon as={AiOutlineLink} boxSize="9px" />
                                                        <Text>SIM</Text>
                                                    </HStack>
                                                </Badge>
                                            ) : (
                                                <Badge bg="rgba(160,163,168,0.14)" color="textSecondary" border="1px solid" borderColor="borderPrimary" fontSize="9px" fontWeight="800" borderRadius="md" px={1.5}>
                                                    <HStack spacing={1}>
                                                        <Icon as={AiOutlineDisconnect} boxSize="9px" />
                                                        <Text>NÃO</Text>
                                                    </HStack>
                                                </Badge>
                                            )}
                                        </Td>
                                        <Td borderColor="borderPrimary" isNumeric>
                                            <HStack spacing={1} justify="flex-end" onClick={e => e.stopPropagation()}>
                                                {papel === 'neutro' && (
                                                    <>
                                                        <Tooltip label="Marcar como concluída" fontSize="xs">
                                                            <IconButton
                                                                aria-label="Concluir"
                                                                icon={<AiOutlineCheckCircle />}
                                                                size="xs"
                                                                variant="ghost"
                                                                color="textSecondary"
                                                                _hover={{ color: 'secondary', bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100' }}
                                                                onClick={() => setAcaoConfirmar({ tipo: 'concluir', atividade: atv })}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label="Cancelar atividade" fontSize="xs">
                                                            <IconButton
                                                                aria-label="Cancelar"
                                                                icon={<AiOutlineCloseCircle />}
                                                                size="xs"
                                                                variant="ghost"
                                                                color="textSecondary"
                                                                _hover={{ color: '#FC8181', bg: 'rgba(252,129,129,0.1)' }}
                                                                onClick={() => setAcaoConfirmar({ tipo: 'cancelar', atividade: atv })}
                                                            />
                                                        </Tooltip>
                                                    </>
                                                )}
                                                <Tooltip label="Visualizar" fontSize="xs">
                                                    <IconButton
                                                        aria-label="Visualizar"
                                                        icon={<AiOutlineEye />}
                                                        size="xs"
                                                        variant="ghost"
                                                        color="textSecondary"
                                                        _hover={{ color: 'secondary', bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100' }}
                                                        onClick={() => handleVisualizar(atv)}
                                                    />
                                                </Tooltip>
                                                {(() => {
                                                    const motivoEdicao = motivoBloqueioEdicao(atv);
                                                    return (
                                                        <Tooltip label={motivoEdicao || 'Editar'} fontSize="xs" shouldWrapChildren>
                                                            <IconButton
                                                                aria-label="Editar"
                                                                icon={<AiOutlineEdit />}
                                                                size="xs"
                                                                variant="ghost"
                                                                color="textSecondary"
                                                                _hover={{ color: 'secondary', bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100' }}
                                                                isDisabled={!!motivoEdicao}
                                                                onClick={() => handleAbrir(atv)}
                                                            />
                                                        </Tooltip>
                                                    );
                                                })()}
                                                <Tooltip label="Excluir" fontSize="xs">
                                                    <IconButton
                                                        aria-label="Excluir"
                                                        icon={<AiOutlineDelete />}
                                                        size="xs"
                                                        variant="ghost"
                                                        color="textSecondary"
                                                        _hover={{ color: 'red.400', bg: 'rgba(229,62,62,0.1)' }}
                                                        onClick={() => handleAbrirExcluir(atv)}
                                                    />
                                                </Tooltip>
                                            </HStack>
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </TableContainer>

                {/* Paginação configurável compartilhada (5/15/30/50) */}
                <TablePagination
                    page={currentPage}
                    pageSize={itemsPerPage}
                    totalItems={atividadesFiltradas.length}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
                />
            </Box>

            {/* -- Modal Visualizar Atividade -- */}
            <Modal isOpen={isViewOpen} onClose={handleFecharVisualizar} isCentered size="2xl" scrollBehavior="inside">
                <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.700" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl" overflow="hidden">
                    <ModalCloseButton color="textSecondary" zIndex={2} />
                    <ModalBody pt={0} pb={2} px={6}>
                        {atividadeVisualizando && (() => {
                            const meta = getStatusMeta(atividadeVisualizando);
                            const tipoCor = resolveTipoCor(atividadeVisualizando.tipo);
                            const marcaLogo = marcas.find(m => (m.nome || '').toLowerCase() === (atividadeVisualizando.empresa || '').toLowerCase())?.logo;
                            const pipelineStatus = pipelineLinkStatus(atividadeVisualizando);
                            return (
                                <VStack spacing={5} align="stretch">
                                    {/* Hero header */}
                                    <Box
                                        mx={-6}
                                        pt={10}
                                        pb={5}
                                        px={6}
                                        borderTopRadius="2xl"
                                        bgGradient={`linear(to-b, ${tipoCor}26, transparent)`}
                                    >
                                        <Flex direction="column" align="center" gap={3}>
                                            <Flex
                                                boxSize="62px"
                                                borderRadius="2xl"
                                                align="center"
                                                justify="center"
                                                bg={`${tipoCor}1A`}
                                                border="1px solid"
                                                borderColor={`${tipoCor}40`}
                                                boxShadow={`0 10px 28px ${tipoCor}22`}
                                            >
                                                <Icon as={meta.icon || AiOutlineCalendar} color={tipoCor} boxSize="30px" />
                                            </Flex>
                                            <VStack spacing={2}>
                                                <Text fontSize="xl" fontWeight="900" color="textPrimary" textAlign="center" lineHeight="1.2">
                                                    {atividadeVisualizando.nome}
                                                </Text>
                                                <HStack spacing={2} flexWrap="wrap" justify="center">
                                                    <Badge bg={`${meta.cor}20`} color={meta.cor} fontSize="xs" fontWeight="800" borderRadius="full" px={2.5} py={0.5}>
                                                        <HStack spacing={1}>
                                                            <Icon as={meta.icon} boxSize="10px" />
                                                            <Text>{meta.label}</Text>
                                                        </HStack>
                                                    </Badge>
                                                    <Badge bg={`${tipoCor}20`} color={tipoCor} fontSize="xs" fontWeight="800" borderRadius="full" px={2.5} py={0.5}>
                                                        {atividadeVisualizando.tipo}
                                                    </Badge>
                                                    {atividadeVisualizando.ligarPipeline && (
                                                        <Badge bg="rgba(1,233,117,0.15)" color="secondary" fontSize="xs" fontWeight="800" borderRadius="full" px={2.5} py={0.5}>
                                                            <HStack spacing={1}>
                                                                <Icon as={AiOutlinePartition} boxSize="10px" />
                                                                <Text>Pipeline</Text>
                                                            </HStack>
                                                        </Badge>
                                                    )}
                                                </HStack>
                                                {atividadeVisualizando.empresa && (
                                                    <HStack
                                                        spacing={2}
                                                        color="textSecondary"
                                                        bg={colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.50'}
                                                        px={2.5}
                                                        py={1}
                                                        borderRadius="full"
                                                        border="1px solid"
                                                        borderColor="borderPrimary"
                                                    >
                                                        {marcaLogo ?(
                                                            <Box boxSize="18px" borderRadius="full" bg="white" overflow="hidden" flexShrink={0}>
                                                                <Image
                                                                    src={marcaLogo}
                                                                    alt={atividadeVisualizando.empresa}
                                                                    boxSize="18px"
                                                                    objectFit="contain"
                                                                    fallback={<Icon as={AiOutlineBank} boxSize="12px" color="gray.500" />}
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <Icon as={AiOutlineBank} boxSize="12px" />
                                                        )}
                                                        <Text fontSize="xs" fontWeight="800" color="textPrimary">{atividadeVisualizando.empresa}</Text>
                                                    </HStack>
                                                )}
                                            </VStack>
                                        </Flex>
                                    </Box>

                                    {/* Details */}
                                    <SimpleGrid columns={2} spacing={3}>
                                        <DetailItem
                                            icon={AiOutlineCalendar}
                                            label="Início"
                                            accent="#4299E1"
                                            value={`${fmtData(atividadeVisualizando.dataInicio)}${atividadeVisualizando.horaInicio && atividadeVisualizando.horaInicio !== '00:00' ?` às ${atividadeVisualizando.horaInicio}` : ''}`}
                                        />
                                        <DetailItem
                                            icon={AiOutlineCalendar}
                                            label="Fim"
                                            accent="#FC8181"
                                            value={`${fmtData(atividadeVisualizando.dataFim || atividadeVisualizando.dataInicio)}${atividadeVisualizando.horaFim && atividadeVisualizando.horaFim !== '00:00' ?` às ${atividadeVisualizando.horaFim}` : ''}`}
                                        />
                                        <DetailItem
                                            icon={AiOutlineUser}
                                            label="Responsável"
                                            accent="#9F7AEA"
                                            value={atividadeVisualizando.responsavel}
                                            leftElement={
                                                <Avatar
                                                    size="sm"
                                                    name={atividadeVisualizando.responsavel || '?'}
                                                    bg="#9F7AEA25"
                                                    color="#9F7AEA"
                                                    fontWeight="900"
                                                    flexShrink={0}
                                                />
                                            }
                                        />
                                        <DetailItem
                                            icon={AiOutlineBank}
                                            label="Empresa / Marca"
                                            accent="#ED8936"
                                            value={atividadeVisualizando.empresa}
                                            leftElement={marcaLogo ?(
                                                <Flex
                                                    flexShrink={0}
                                                    boxSize="38px"
                                                    borderRadius="lg"
                                                    align="center"
                                                    justify="center"
                                                    bg="white"
                                                    border="1px solid"
                                                    borderColor="borderPrimary"
                                                    overflow="hidden"
                                                >
                                                    <Image
                                                        src={marcaLogo}
                                                        alt={atividadeVisualizando.empresa}
                                                        boxSize="38px"
                                                        objectFit="contain"
                                                        p={1}
                                                        fallback={<Icon as={AiOutlineBank} boxSize="20px" color="gray.500" />}
                                                    />
                                                </Flex>
                                            ) : undefined}
                                        />
                                        <DetailItem
                                            icon={AiOutlinePartition}
                                            label="Pipeline"
                                            accent={pipelineStatus.semFunil ?'#FC8181' : '#01E975'}
                                            value={pipelineStatus.semFunil ?'Sem funil' : (atividadeVisualizando.ligarPipeline ?'Vinculada' : 'Não vinculada')}
                                            valueColor={pipelineStatus.semFunil ?'#FC8181' : (atividadeVisualizando.ligarPipeline ?'secondary' : 'textSecondary')}
                                            gridColumn={{ md: "span 2" }}
                                        />
                                    </SimpleGrid>

                                    {/* Observação */}
                                    {atividadeVisualizando.observacao && (
                                        <Box bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"} border="1px solid" borderColor="borderPrimary" borderRadius="xl" px={4} py={3}>
                                            <HStack spacing={1.5} mb={1}>
                                                <Icon as={AiOutlineFileText} color="textSecondary" boxSize="11px" />
                                                <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px">
                                                    Observação
                                                </Text>
                                            </HStack>
                                            <Text fontSize="sm" color="textPrimary" whiteSpace="pre-wrap" lineHeight="1.5">
                                                {atividadeVisualizando.observacao}
                                            </Text>
                                        </Box>
                                    )}
                                </VStack>
                            );
                        })()}
                    </ModalBody>
                    <ModalFooter
                        borderTop="1px solid"
                        borderColor="borderPrimary"
                        mt={2}
                        px={6}
                        py={3}
                        gap={{ base: 3, md: 5 }}
                        flexDirection={{ base: 'column', md: 'row' }}
                        alignItems={{ base: 'stretch', md: 'center' }}
                        justifyContent="space-between"
                    >
                        <Flex gap={3} flexWrap="wrap" w={{ base: 'full', md: 'auto' }}>
                            <Button
                                {...VIEW_FOOTER_BUTTON_PROPS}
                                leftIcon={<Icon as={AiOutlineDelete} boxSize="14px" />}
                                variant="ghost"
                                color="red.400"
                                _hover={{ bg: 'rgba(229,62,62,0.1)' }}
                                onClick={handleExcluirDoView}
                            >
                                Excluir
                            </Button>
                            <Button
                                leftIcon={<Icon as={AiOutlineCalendar} boxSize="14px" />}
                                {...VIEW_FOOTER_BUTTON_PROPS}
                                variant="ghost"
                                color="textSecondary"
                                title="Ver no Calendário"
                                _hover={{ bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100', color: 'textPrimary' }}
                                onClick={() => {
                                    handleFecharVisualizar();
                                    navigate(`/calendario?date=${atividadeVisualizando.dataInicio}`);
                                }}
                            >
                                Calendário
                            </Button>
                        </Flex>

                        <Flex
                            gap={3}
                            flexWrap="wrap"
                            w={{ base: 'full', md: 'auto' }}
                            justifyContent={{ base: 'flex-start', md: 'flex-end' }}
                        >
                            <Button
                                {...VIEW_FOOTER_BUTTON_PROPS}
                                variant="ghost"
                                color="textSecondary"
                                _hover={{ bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100' }}
                                onClick={handleFecharVisualizar}
                            >
                                Fechar
                            </Button>
                            {atividadeVisualizando && papelDeAtividade(atividadeVisualizando) === 'neutro' && (
                                <>
                                    <Button
                                        {...VIEW_FOOTER_BUTTON_PROPS}
                                        leftIcon={<Icon as={AiOutlineCloseCircle} boxSize="14px" />}
                                        variant="outline"
                                        borderColor="rgba(252,129,129,0.45)"
                                        color="#FC8181"
                                        _hover={{ bg: 'rgba(252,129,129,0.1)' }}
                                        isDisabled={atividadePassada}
                                        title={atividadePassada ?'Eventos passados não podem ser cancelados' : undefined}
                                        onClick={() => {
                                            if (atividadePassada) return;
                                            setAcaoConfirmar({ tipo: 'cancelar', atividade: atividadeVisualizando, fecharFicha: true });
                                        }}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        {...VIEW_FOOTER_BUTTON_PROPS}
                                        leftIcon={<Icon as={AiOutlineCheckCircle} boxSize="14px" />}
                                        variant="outline"
                                        borderColor="rgba(1,233,117,0.45)"
                                        color="secondary"
                                        _hover={{ bg: 'secondarySubtle' }}
                                        isDisabled={atividadePassada}
                                        title={atividadePassada ?'Eventos passados não podem ser concluídos' : undefined}
                                        onClick={() => {
                                            if (atividadePassada) return;
                                            setAcaoConfirmar({ tipo: 'concluir', atividade: atividadeVisualizando, fecharFicha: true });
                                        }}
                                    >
                                        Concluir
                                    </Button>
                                </>
                            )}
                            <Button
                                {...VIEW_FOOTER_BUTTON_PROPS}
                                leftIcon={<Icon as={AiOutlineEdit} boxSize="14px" />}
                                variant="primary"
                                isDisabled={!!bloqueioEdicaoView}
                                title={bloqueioEdicaoView || undefined}
                                onClick={() => { if (!bloqueioEdicaoView) handleEditarDoView(); }}
                            >
                                Editar
                            </Button>
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* -- Modal Criar/Editar -- */}
            <Modal isOpen={isOpen} onClose={handleMinimizar} isCentered size="lg" scrollBehavior="inside" closeOnOverlayClick={true}>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl">
                    <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" pr="90px">
                        <HStack spacing={3}>
                            <Icon as={AiOutlineCalendar} color="secondary" boxSize="20px" />
                            <Text fontWeight="800">{atividadeEditando ? 'Editar Atividade' : 'Criar Evento'}</Text>
                        </HStack>
                    </ModalHeader>
                    <HStack position="absolute" top={4} right={4} spacing={1} zIndex={10}>
                        <IconButton
                            aria-label="Minimizar"
                            title="Minimizar"
                            icon={<Icon as={AiOutlineMinus} boxSize="16px" />}
                            size="sm"
                            variant="ghost"
                            color="textSecondary"
                            _hover={{ bg: "whiteAlpha.200", color: "secondary" }}
                            borderRadius="md"
                            onClick={handleMinimizar}
                        />
                        <ModalCloseButton position="relative" top={0} right={0} color="textSecondary" onClick={handleFechar} />
                    </HStack>
                    <ModalBody py={5}>
                        <VStack spacing={4} align="stretch">
                            {/* Nome do evento */}
                            <FormControl isRequired>
                                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Nome do evento
                                </FormLabel>
                                <Input
                                    value={form.nome}
                                    onChange={e => setForm(p => ({ ...p, nome: e.target.value }))}
                                    placeholder="Ex.: Reunião de onboarding"
                                    bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    color="textPrimary"
                                    _placeholder={{ color: 'textSecondary' }}
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                />
                            </FormControl>

                            {/* Início */}
                            <FormControl isRequired>
                                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px" mb={1}>
                                    Início
                                </FormLabel>
                                <HStack spacing={3}>
                                    <Input
                                        type="date"
                                        value={form.dataInicio}
                                        onChange={e => setForm(p => ({ ...p, dataInicio: e.target.value }))}
                                        bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        color="textPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                        sx={{ colorScheme: colorMode }}
                                        flex={2}
                                    />
                                    <Input
                                        type="time"
                                        value={form.horaInicio}
                                        onChange={e => setForm(p => ({ ...p, horaInicio: e.target.value }))}
                                        bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        color="textPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                        sx={{ colorScheme: colorMode }}
                                        flex={1}
                                    />
                                </HStack>
                            </FormControl>

                            {/* Fim */}
                            <FormControl>
                                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px" mb={1}>
                                    Fim
                                </FormLabel>
                                <HStack spacing={3}>
                                    <Input
                                        type="date"
                                        value={form.dataFim}
                                        onChange={e => setForm(p => ({ ...p, dataFim: e.target.value }))}
                                        bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        color="textPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                        sx={{ colorScheme: colorMode }}
                                        flex={2}
                                    />
                                    <Input
                                        type="time"
                                        value={form.horaFim}
                                        onChange={e => setForm(p => ({ ...p, horaFim: e.target.value }))}
                                        bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        color="textPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                        sx={{ colorScheme: colorMode }}
                                        flex={1}
                                    />
                                </HStack>
                            </FormControl>

                            {/* Tipo e Status (Dropdowns do banco) */}
                            <HStack spacing={3}>
                                <FormControl>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Tipo
                                    </FormLabel>
                                    <SearchableSelect
                                        placeholder="Selecione o tipo"
                                        value={form.tipo_id || ''}
                                        onChange={(v, opt) => {
                                            const id = v == null ?null : Number(v);
                                            setForm(p => ({ ...p, tipo_id: id, tipo: opt ?opt.label : '' }));
                                        }}
                                        options={tipos.map(t => ({ value: t.id, label: t.nome }))}
                                        isClearable
                                        bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        color="textPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Status
                                    </FormLabel>
                                    <SearchableSelect
                                        placeholder="Selecione o status"
                                        value={form.status_id || ''}
                                        onChange={(v, opt) => {
                                            const id = v == null ?null : Number(v);
                                            setForm(p => ({ ...p, status_id: id, status: opt ?opt.label : '' }));
                                        }}
                                        options={statusList.map(s => ({ value: s.id, label: s.nome }))}
                                        isClearable
                                        bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                                        borderColor="borderPrimary"
                                        color="textPrimary"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                </FormControl>
                            </HStack>

                            {/* Responsável (Lista de Colaboradores) */}
                            <FormControl>
                                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Responsável
                                </FormLabel>
                                <SearchableSelect
                                    placeholder="Selecione o colaborador"
                                    value={form.assignee_id || ''}
                                    onChange={(v, opt) => {
                                        const id = v == null ?null : Number(v);
                                        setForm(p => ({
                                            ...p,
                                            assignee_id: id,
                                            responsavel: opt ?opt.label : ''
                                        }));
                                    }}
                                    options={employees.map(e => ({ value: e.id, label: e.name }))}
                                    isClearable
                                    bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    color="textPrimary"
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                />
                            </FormControl>

                            {/* Empresa (Marcas ERP) - Autocomplete */}
                            <FormControl position="relative">
                                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Empresa / Marca
                                </FormLabel>
                                <Input
                                    value={form.empresa}
                                    onChange={e => setForm(p => ({ ...p, empresa: e.target.value, company_id: null }))}
                                    onFocus={() => setEmpresaFocada(true)}
                                    onBlur={() => setTimeout(() => setEmpresaFocada(false), 250)}
                                    placeholder="Digite para buscar marca..."
                                    bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    color="textPrimary"
                                    _placeholder={{ color: 'textSecondary' }}
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                />
                                {empresaFocada && (marcasSugeridas.length > 0 || marcasLoading || (marcas || []).length === 0 || !!form.empresa?.trim()) && (
                                    <Box
                                        position="absolute"
                                        top="100%"
                                        left={0}
                                        right={0}
                                        bg="surface"
                                        border="1px solid"
                                        borderColor="borderPrimary"
                                        borderRadius="md"
                                        zIndex={2000}
                                        maxH="200px"
                                        overflowY="auto"
                                        boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                                    >
                                        {marcasSugeridas.map(m => (
                                            <Box
                                                key={m.id}
                                                px={3}
                                                py={2}
                                                cursor="pointer"
                                                _hover={{ bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100', color: 'secondary' }}
                                                onMouseDown={() => {
                                                    setForm(p => ({ ...p, empresa: m.nome, company_id: m.id }));
                                                    setEmpresaFocada(false);
                                                }}
                                                fontSize="sm"
                                                color="textPrimary"
                                            >
                                                {m.nome}
                                            </Box>
                                        ))}
                                        {/* Estados visíveis do autocomplete — a busca nunca fica "muda" */}
                                        {marcasSugeridas.length === 0 && marcasLoading && (
                                            <HStack px={3} py={2.5} spacing={2}>
                                                <Icon as={AiOutlineSync} boxSize="12px" color="textSecondary" />
                                                <Text fontSize="xs" color="textSecondary">Carregando marcas do ERP...</Text>
                                            </HStack>
                                        )}
                                        {marcasSugeridas.length === 0 && !marcasLoading && (marcas || []).length === 0 && (
                                            <Box
                                                px={3}
                                                py={2.5}
                                                cursor="pointer"
                                                _hover={{ bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100' }}
                                                onMouseDown={(e) => { e.preventDefault(); refetchMarcas(); }}
                                            >
                                                <Text fontSize="xs" color="#FC8181" fontWeight="700">
                                                    Não foi possível carregar as marcas.
                                                </Text>
                                                <Text fontSize="xs" color="textSecondary">Clique aqui para tentar novamente.</Text>
                                            </Box>
                                        )}
                                        {marcasSugeridas.length === 0 && !marcasLoading && (marcas || []).length > 0 && !!form.empresa?.trim() && (
                                            <Box px={3} py={2.5}>
                                                <Text fontSize="xs" color="textSecondary">Nenhuma marca encontrada com esse nome.</Text>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="xs" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    <HStack spacing={2}>
                                        <Icon as={AiOutlinePartition} color="secondary" boxSize="13px" />
                                        <Text>Negócio vinculado (Pipeline)</Text>
                                    </HStack>
                                </FormLabel>
                                <SearchableSelect
                                    value={form.cardId || ''}
                                    onChange={v => {
                                        const id = v || null;
                                        const card = (pipelineCards || []).find(c => String(c.id) === String(id));
                                        setForm(p => ({
                                            ...p,
                                            cardId: id,
                                            ligarPipeline: !!id,
                                            // puxa a empresa do negócio, se houver
                                            company_id: card?.company_id || p.company_id,
                                            empresa: card?.empresa || p.empresa,
                                        }));
                                    }}
                                    options={(pipelineCards || []).filter(c => !c.completed).map(c => ({
                                        value: c.id,
                                        label: c.titulo || 'Negócio',
                                        description: c.empresa || '',
                                    }))}
                                    placeholder="Nenhum (atividade avulsa)"
                                    isClearable
                                    bg={colorMode === 'dark' ?"whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    color="textPrimary"
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                />
                                <Text fontSize="10px" color="textSecondary" mt={1.5}>
                                    A atividade aparece dentro do negócio no funil. Deixe em branco para uma atividade avulsa.
                                </Text>
                            </FormControl>

                            <AnotacaoFormSection
                                isEditing={!!atividadeEditando}
                                existingValue={atividadeEditando?.observacao || ''}
                                newValue={novaAnotacao}
                                onNewValueChange={setNovaAnotacao}
                                entityLabel="atividade"
                            />
                        </VStack>
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100' }} onClick={handleFechar} size="sm">
                            Cancelar
                        </Button>
                        <Button bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={handleSalvar} size="sm">
                            {atividadeEditando ?'Salvar Alterações' : 'Criar Atividade'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* -- Confirmação padrão de exclusão -- */}
            <ConfirmDeleteDialog
                isOpen={isDelOpen}
                onClose={onDelClose}
                onConfirm={handleConfirmarExcluir}
                title="Excluir Atividade"
                itemName={atividadeExcluindo?.nome}
                warning={atividadeExcluindo && !!(pipelineCards || []).find(c => String(c.id) === String(atividadeExcluindo.cardId))}
                description={(() => {
                    if (!atividadeExcluindo) return "";
                    const negocioVinculado = (pipelineCards || []).find(c => String(c.id) === String(atividadeExcluindo.cardId));
                    return negocioVinculado 
                        ? `Atenção: Esta atividade está vinculada ao negócio "${negocioVinculado.titulo}". Ao excluir, esta atividade será desvinculada.`
                        : "";
                })()}
            />

            {/* Confirmação padrão de ação (concluir/cancelar) — evita cliques acidentais */}
            <ConfirmActionDialog
                isOpen={!!acaoConfirmar}
                onClose={() => setAcaoConfirmar(null)}
                onConfirm={executarAcaoConfirmada}
                title={acaoConfirmar?.tipo === 'concluir' ? 'Concluir Atividade' : 'Cancelar Atividade'}
                itemName={acaoConfirmar?.atividade?.nome}
                description={acaoConfirmar?.tipo === 'concluir'
                    ? 'A atividade será marcada como CONCLUÍDA e passa a contar nos Relatórios.'
                    : 'A atividade será marcada como CANCELADA e não poderá mais ser editada.'}
                confirmLabel={acaoConfirmar?.tipo === 'concluir' ? 'Sim, concluir' : 'Sim, cancelar'}
                scheme={acaoConfirmar?.tipo === 'concluir' ? 'green' : 'red'}
                icon={acaoConfirmar?.tipo === 'concluir' ? AiOutlineCheckCircle : AiOutlineCloseCircle}
            />
        </VStack>
    );
};

export default Atividades;
